import { motion } from 'framer-motion';
import {
  Clock, Megaphone, FileText, Inbox, UploadCloud, LayoutGrid, MessageCircle,
  Star, type LucideIcon,
} from 'lucide-react';
import type { AssistantIntent } from '../../lib/assistantIntents';

const INTENT_ICON: Record<string, LucideIcon> = {
  'change-business-hours': Clock,
  'publish-announcement': Megaphone,
  'edit-homepage-copy': FileText,
  'check-new-leads': Inbox,
  'publish-site': UploadCloud,
  'create-content-section': LayoutGrid,
  'update-whatsapp-message': MessageCircle,
};

export type Suggestion = {
  intent: AssistantIntent;
  isFavorite: boolean;
};

/**
 * Acciones frecuentes del home: chips accionables que disparan el mismo
 * flujo del composer (ruteo / prefill / confirmación). Máximo recomendado
 * 4–5 visibles (sección 7.4).
 *
 * Mobile: scroll horizontal con cards de ancho fijo para no truncar labels.
 * Desktop (rail): lista vertical con descripción.
 */
export default function SuggestedActions({
  suggestions,
  limit = 5,
  onSelect,
  onToggleFavorite,
}: {
  suggestions: Suggestion[];
  limit?: number;
  onSelect: (intent: AssistantIntent) => void;
  onToggleFavorite: (id: string) => void;
}) {
  const visible = suggestions.slice(0, limit);
  if (visible.length === 0) return null;

  return (
    <>
      {/* Mobile: scroll horizontal */}
      <div className="lg:hidden flex gap-2.5 overflow-x-auto -mx-4 px-4 scrollbar-none">
        {visible.map(({ intent, isFavorite }, index) => {
          const Icon = INTENT_ICON[intent.id] ?? Megaphone;
          return (
            <motion.div
              key={intent.id}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.16, delay: index * 0.03 }}
              className="group relative flex-shrink-0 w-32 rounded-xl border border-ui-border bg-ui-surface hover:border-ui-accent/60 transition-colors overflow-hidden"
            >
              <button
                type="button"
                onClick={() => onSelect(intent)}
                className="w-full flex flex-col items-center gap-2 px-2 pt-3 pb-2.5 text-center"
              >
                <span className="w-9 h-9 flex items-center justify-center rounded-xl bg-ui-accent/10 text-ui-accent">
                  <Icon size={17} strokeWidth={1.85} />
                </span>
                <span className="text-sm font-medium text-ui-text leading-snug">{intent.label}</span>
              </button>
              <button
                type="button"
                onClick={() => onToggleFavorite(intent.id)}
                aria-label={isFavorite ? 'Quitar de favoritos' : 'Fijar como favorito'}
                aria-pressed={isFavorite}
                className={`absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded-md transition-colors ${
                  isFavorite
                    ? 'text-ui-accent'
                    : 'text-ui-text-subtle opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-ui-text'
                }`}
              >
                <Star size={12} fill={isFavorite ? 'currentColor' : 'none'} />
              </button>
            </motion.div>
          );
        })}
      </div>

      {/* Desktop rail: lista vertical */}
      <div className="hidden lg:flex flex-col gap-2.5">
        {visible.map(({ intent, isFavorite }, index) => {
          const Icon = INTENT_ICON[intent.id] ?? Megaphone;
          return (
            <motion.div
              key={intent.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.16, delay: index * 0.02 }}
              className="group relative flex items-center gap-3 rounded-xl border border-ui-border bg-ui-surface px-3 py-2.5 hover:border-ui-accent/60 transition-colors"
            >
              <button
                type="button"
                onClick={() => onSelect(intent)}
                className="flex flex-1 items-center gap-3 min-w-0 text-left"
              >
                <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-ui-accent/10 text-ui-accent">
                  <Icon size={16} strokeWidth={1.85} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-ui-text truncate">{intent.label}</span>
                  <span className="block text-sm text-ui-text-subtle truncate">{intent.description}</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => onToggleFavorite(intent.id)}
                aria-label={isFavorite ? 'Quitar de favoritos' : 'Fijar como favorito'}
                aria-pressed={isFavorite}
                className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                  isFavorite
                    ? 'text-ui-accent'
                    : 'text-ui-text-subtle opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-ui-text'
                }`}
              >
                <Star size={15} fill={isFavorite ? 'currentColor' : 'none'} />
              </button>
            </motion.div>
          );
        })}
      </div>
    </>
  );
}
